import Head from 'next/head';
import { Page, Tabs } from '@zillow/constellation-10';
import { emitYourRentalTabClick, YourRentalPageTriggerSource } from '../../analytics/events';
import ErrorPage from './ErrorPage';
import { useYourRentalPageContext } from '../../contexts/YourRentalPageContext';

export const TABS = {
  rent: 'Rent',
  documents: 'Documents',
} as const;
export type TABS = (typeof TABS)[keyof typeof TABS];

type YourRentalPageLayoutProps = {
  header: React.ReactNode;
  rentTabContent: React.ReactNode;
  documentsTabContent: React.ReactNode;
  selectedTab?: string;
  onTabChange?: (value: string) => void;
  isError?: boolean;
};

const YourRentalPageLayout = ({
  header,
  rentTabContent,
  documentsTabContent,
  selectedTab,
  onTabChange,
  isError = false,
}: YourRentalPageLayoutProps) => {
  const { renterProfile } = useYourRentalPageContext();

  const handleTabChange = (value: string) => {
    if (value === TABS.rent) {
      emitYourRentalTabClick(YourRentalPageTriggerSource.RENT_TAB, renterProfile);
    } else if (value === TABS.documents) {
      emitYourRentalTabClick(YourRentalPageTriggerSource.DOCUMENTS_TAB, renterProfile);
    }
    onTabChange?.(value);
  };

  const tabsRootProps = selectedTab
    ? { selected: selectedTab, onSelectedChange: handleTabChange }
    : { defaultSelected: TABS.rent, onSelectedChange: handleTabChange };

  return (
    <>
      <Head>
        <title>Your Rental</title>
      </Head>
      <Page.Root
        css={{
          background: 'transparent',
        }}
      >
        {isError ? (
          <ErrorPage />
        ) : (
          <>
            <Page.Header>{header}</Page.Header>
            <Page.Content css={{ marginTop: '0' }}>
              <Tabs.Root {...tabsRootProps} aria-label="Your rental information tabs">
                <Tabs.List aria-label="Switch between rent and documents sections">
                  <Tabs.Tab data-testid="RentTab" value={TABS.rent}>
                    {TABS.rent}
                  </Tabs.Tab>
                  <Tabs.Tab data-testid="DocumentsTab" value={TABS.documents}>
                    {TABS.documents}
                  </Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value={TABS.rent} css={{ paddingLeft: '0', paddingRight: '0' }}>
                  {rentTabContent}
                </Tabs.Panel>
                <Tabs.Panel value={TABS.documents} css={{ paddingLeft: '0', paddingRight: '0' }}>
                  {documentsTabContent}
                </Tabs.Panel>
              </Tabs.Root>
            </Page.Content>
          </>
        )}
      </Page.Root>
    </>
  );
};

export default YourRentalPageLayout;
